import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getAdultHealthSummary } from '../HedisMetricService';
import AdultHealthSummary from '../../../Models/HedisMetrics/AdultHealthSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const AdultHealthSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [adultHealthSummary, setAdultHealthSummary] = useState<AdultHealthSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        adultHealthHealthPlans,
        setAdultHealthHealthPlans,
        adultHealthTeam,
        setAdultHealthTeam,
        adultHealthDateFilter,
        setAdultHealthDateFilter,
        adultHealthFromDate,
        setAdultHealthFromDate,
        adultHealthToDate,
        setAdultHealthToDate,
        adultHealthIncludePastOpenStatus,
        setAdultHealthIncludePastOpenStatus,
        adultHealthShowInActiveTeam,
        setAdultHealthShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend('Adult Health Summary', 'Visits vs Not Visits', '', 'Count', [{ name: '', data: [0] }], [''], null, chartHeight)
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (adultHealthDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(adultHealthFromDate) && Boolean(adultHealthToDate))) {
            const changeFilter = async () => {
                let start = adultHealthFromDate !== null ? new Date(adultHealthFromDate).toDateString() : null;
                let end = adultHealthToDate !== null ? new Date(adultHealthToDate).toDateString() : null;
                const data = await getAdultHealthSummary(
                    adultHealthHealthPlans,
                    adultHealthTeam,
                    adultHealthDateFilter,
                    start,
                    end,
                    adultHealthIncludePastOpenStatus,
                    adultHealthShowInActiveTeam
                );
                setAdultHealthSummary(data);
                setIsPrepared(true);
            };
            if ((adultHealthFromDate !== null || adultHealthToDate !== null) && adultHealthToDate < adultHealthFromDate) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Adult Health Summary',
                        'Visits vs Not Visits',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        adultHealthHealthPlans,
        adultHealthTeam,
        adultHealthDateFilter,
        adultHealthFromDate,
        adultHealthToDate,
        adultHealthIncludePastOpenStatus,
        adultHealthShowInActiveTeam,
    ]);

    useEffect(() => {
        if (adultHealthSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: adultHealthSummary.VisitsCompleted,
                name: 'Visits Completed',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Visits Completed', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: adultHealthSummary.NoVisits,
                name: 'No Visits',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'No Visits', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            if (adultHealthSummary.VisitsCompleted === 0 && adultHealthSummary.NoVisits === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Adult Health Summary',
                        'Visits vs Not Visits',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Adult Health Summary', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adultHealthSummary, adultHealthHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(adultHealthHealthPlans);
        setTeam(adultHealthTeam);
        setFilter(adultHealthDateFilter);
        setFDate(adultHealthFromDate);
        setTDate(adultHealthToDate);
        setIncludePastOpenStatus(adultHealthIncludePastOpenStatus);
        setShowInActiveTeam(adultHealthShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.ProviderVisits);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/ProviderVisits`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Adult Health
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">Member is 20 years of age or older, within a rolling 12 months</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Visits Completed :
                                    </Typography>

                                    <Typography variant="caption">
                                        Any of the CPT codes - 92002, 92004, 92012, 92014, 93005, 97597, 98966, 98967, 98968, 98969, 98970, 98971, 98972, 99211,
                                        99212, 99213, 99214, 99215, 99201, 99202, 99203, 99204, 99205, 99223, 99241, 99242, 99243, 99244, 99245, 99281, 99282,
                                        99283, 99284, 99285, 99304, 99305, 99306, 99307, 99308, 99309, 99310, 99334, 99335, 99336, 99337, 99341, 99342, 99343,
                                        99344, 99345, 99347, 99348, 99349, 99350, 99381, 99382, 99383, 99384, 99385, 99386, 99387, 99391, 99392, 99393, 99394,
                                        99395, 99396, 99397, 99401, 99402, 99403, 99404, 99411, 99412, 99421, 99422, 99423, 99429, 99441, 99442, 99443, 99444,
                                        99457, 99483, G0402, G0438, G0439, G0463, T1015, H1000, G0071, G2010, G2012, G2061, G2062, G2063, G2250, G2251, G2252
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        No Visits :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={adultHealthTeam}
                        setSelectedTeam={setAdultHealthTeam}
                        dateFilter={adultHealthDateFilter}
                        setDateFilter={setAdultHealthDateFilter}
                        fromDate={adultHealthFromDate}
                        setFromDate={setAdultHealthFromDate}
                        toDate={adultHealthToDate}
                        setToDate={setAdultHealthToDate}
                        healthPlan={adultHealthHealthPlans}
                        setHealthPlan={setAdultHealthHealthPlans}
                        includePastOpenStatusMembers={adultHealthIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setAdultHealthIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.ProviderVisits}
                        showInActiveTeams={adultHealthShowInActiveTeam}
                        setShowInActiveTeams={setAdultHealthShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default AdultHealthSummaries;
